import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminRolesView from './AdminRolesView';

const AdminRolesContainer = () => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitLoading, setSubmitLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentRole, setCurrentRole] = useState(null);
    const { isAdmin } = useAuth();
    const [validationErrors, setValidationErrors] = useState({});

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permissions: {}
    });

    // Available permissions that can be assigned
    const availablePermissions = [
        // User management permissions
        { key: 'view_users', label: 'View Users', category: 'User Management' },
        { key: 'add_users', label: 'Add Users', category: 'User Management' },
        { key: 'edit_users', label: 'Edit Users', category: 'User Management' },
        { key: 'delete_users', label: 'Delete Users', category: 'User Management' },
        { key: 'change_user_role', label: 'Change User Role', category: 'User Management' },
        
        // Listing management permissions
        { key: 'view_listings', label: 'View Listings', category: 'Listings' },
        { key: 'edit_listings', label: 'Edit Listings', category: 'Listings' },
        { key: 'approve_listings', label: 'Approve Listings', category: 'Listings' },
        { key: 'claim_listing', label: 'Claim Listing', category: 'Listings' },
        
        // Profile permissions
        { key: 'edit_own_profile', label: 'Edit Own Profile', category: 'Profile' },
        
        // Content permissions
        { key: 'view_seo', label: 'View SEO Content', category: 'Content' },
        { key: 'edit_seo', label: 'Edit SEO Content', category: 'Content' }
    ];

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/roles`);
            setRoles(response.data);
        } catch (err) {
            console.error('Error fetching roles:', err);
            setError('Failed to fetch roles: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (role = null) => {
        setValidationErrors({});
        setError('');
        
        let initialPermissions = {};
        
        if (role) {
            // Edit mode
            setIsEditMode(true);
            setCurrentRole(role);
            
            // Parse existing permissions
            const existingPermissions = typeof role.permissions === 'string'
                ? JSON.parse(role.permissions || '{}')
                : role.permissions || {};
            
            // Set initial permissions from existing role
            initialPermissions = { ...existingPermissions };
        } else {
            // Create mode
            setIsEditMode(false);
            setCurrentRole(null);
        }
        
        const roleName = role ? role.name : '';
        
        // Make sure required permissions are set and can't be changed
        if (roleName === 'manager') {
            initialPermissions.view_users = true;
            initialPermissions.view_listings = true;
            initialPermissions.approve_listings = true;
            initialPermissions.delete_users = true;
        } else if (roleName === 'legal_professional') {
            initialPermissions.view_listings = true;
            initialPermissions.claim_listing = true;
            initialPermissions.edit_own_profile = true;
        } else if (roleName === 'user') {
            initialPermissions.view_listings = true;
            initialPermissions.edit_own_profile = true;
        }
        
        setFormData({
            name: roleName,
            description: role ? (role.description || '') : '',
            permissions: initialPermissions
        });
        
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setValidationErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear validation error when field is edited
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.name.trim()) {
            errors.name = 'Role name is required';
        } else if (formData.name.length < 3) {
            errors.name = 'Role name must be at least 3 characters';
        }
        
        if (!formData.description.trim()) {
            errors.description = 'Description is required';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Check if maximum number of roles is reached
        if (!isEditMode && roles.length >= 4) {
            setError('Maximum number of roles (4) has been reached.');
            return;
        }
        
        if (!validateForm()) {
            return;
        }
        
        setSubmitLoading(true);
        setError('');
        
        try {
            if (isEditMode) {
                if (currentRole.name === 'admin') {
                    setError('Cannot modify admin role');
                    setSubmitLoading(false);
                    return;
                }
                
                const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/roles/${currentRole.id}`, {
                    name: formData.name,
                    description: formData.description
                });
                console.log('Update response:', response.data);
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL}/api/roles`, formData);
            }
            fetchRoles();
            handleCloseModal();
        } catch (err) {
            console.error('Error saving role:', err);
            const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
            setError(`Failed to save role: ${errorMessage}`);
            setSubmitLoading(false);
            return;
        }
        
        setSubmitLoading(false);
    };

    return (
        <AdminRolesView
            roles={roles}
            loading={loading}
            error={error}
            setError={setError}
            isAdmin={isAdmin}
            handleOpenModal={handleOpenModal}
            isModalOpen={isModalOpen}
            handleCloseModal={handleCloseModal}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            submitLoading={submitLoading}
            validationErrors={validationErrors}
            isEditMode={isEditMode}
        />
    );
};

export default AdminRolesContainer;